import React, { useState } from 'react';
import { Spin, Button, message } from 'antd';
import { providerOverrideDelete } from 'api/calendarSetting';
import { IproviderOverrideList, TimeItemType } from 'types/appointmentSetting';
import AddOverrideModal from './components/AddOverrideModal';
import useOverride from 'hooks/useOverride';
import dayjs from 'dayjs';
import trashIcon from 'assets/common/trash.svg';
import pencilIcon from 'assets/common/grayPencil.svg';
import type { IProviderOverrideListWithDates } from './components/AddOverrideModal/types';

const DatesOverride = () => {
    const [openAddAppointmentModal, setOpenAddAppointmentModal] = useState(false);
    const [overrideList, loading, fetchData] = useOverride();
    const [editItem, setEditItem] = useState<IProviderOverrideListWithDates | null>(null);

    function mergeOverrideList(data: IproviderOverrideList[]): IProviderOverrideListWithDates[] {
        const map = new Map();

        data.forEach(({ time, overrideDate, id }) => {
            const key = JSON.stringify(time);

            if (map.has(key)) {
                map.get(key).overrideDates.push({
                    date: overrideDate,
                    id,
                });
            } else {
                map.set(key, { time, overrideDates: [{ date: overrideDate, id }] });
            }
        });

        return Array.from(map.values());
    }

    const mergedOverrideList = mergeOverrideList(overrideList);

    const onCancel = () => {
        setOpenAddAppointmentModal(false);
        setEditItem(null);
    };

    const deleteBlockoutDatesItem = async (item: IProviderOverrideListWithDates) => {
        try {
            const { data } = await providerOverrideDelete(item.overrideDates.map(({ id }) => id));
            if (data.code === 200) {
                message.success(data.message);
                fetchData();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const editDatesItem = (item: IProviderOverrideListWithDates) => {
        setOpenAddAppointmentModal(true);
        setEditItem(item);
    };

    const transferTimePicker = (val) => {
        const { fromHour, fromMinute, toHour, toMinute } = val;
        // eslint-disable-next-line no-nested-ternary
        const startHour = `${fromHour < 10 ? `0${fromHour}` : (fromHour > 12 ? (fromHour === (23 || 22) ? fromHour - 12 : `0${fromHour - 12}`) : fromHour)}`;
        const startMinute = `${fromMinute < 10 ? `0${fromMinute}` : fromMinute}`;
        const startLabel = `${startHour}:${startMinute} ${fromHour >= 12 ? 'PM' : 'AM'}`;

        // eslint-disable-next-line no-nested-ternary
        const endHour = `${toHour < 10 ? `0${toHour}` : (toHour > 12 ? (toHour === (23 || 22) ? toHour - 12 : `0${toHour - 12}`) : toHour)}`;
        const endMinute = `${toMinute < 10 ? `0${toMinute}` : toMinute}`;
        const endLabel = `${endHour}:${endMinute} ${toHour >= 12 ? 'PM' : 'AM'}`;
        return `${startLabel}-${endLabel}`;
    };

    const getOverrideDate = (item: IProviderOverrideListWithDates) => {
        const dayjsDates = item.overrideDates.map(({ date }) => {
            return dayjs(date);
        });
        if (dayjsDates.length === 1) {
            return dayjsDates[0].format('MMMM D, YYYY');
        }

        dayjsDates.sort((a, b) => a.valueOf() - b.valueOf());

        // 分组连续日期
        const groups: dayjs.Dayjs[][] = [];
        let currentGroup: dayjs.Dayjs[] = [dayjsDates[0]];

        for (let i = 1; i < item.overrideDates.length; i++) {
            const currentDate = dayjsDates[i];
            const prevDate = dayjsDates[i - 1];

            // 检查当前日期是否与前一个日期相差一天
            if (currentDate.diff(prevDate, 'day') === 1) {
                currentGroup.push(currentDate);
            } else {
                groups.push([...currentGroup]);
                currentGroup = [currentDate];
            }
        }
        groups.push(currentGroup);

        const formattedGroups = groups.map((group) => {
            if (group.length === 1) {
                // 单个日期
                return group[0].format('MMMM D, YYYY');
            } else {
                // 连续日期
                const firstDate = group[0];
                const lastDate = group[group.length - 1];

                // 检查年份和月份是否相同
                if (firstDate.year() === lastDate.year()) {
                    if (firstDate.month() === lastDate.month()) {
                        // 同年同月
                        return `${firstDate.format('MMMM')} ${firstDate.date()} - ${lastDate.date()}, ${firstDate.year()}`;
                    } else {
                        // 同年不同月
                        return `${firstDate.format('MMMM')} ${firstDate.date()} - ${lastDate.format('MMMM')} ${lastDate.date()}, ${firstDate.year()}`;
                    }
                } else {
                    // 不同年
                    return `${firstDate.format('MMMM D, YYYY')} - ${lastDate.format('MMMM D, YYYY')}`;
                }
            }
        });

        return formattedGroups;
    };

    return (
        <Spin spinning={loading}>
            <div className="mt-7 mb-7">
                {
                    mergedOverrideList && mergedOverrideList.map((item, index) => {
                        const timeArr = item?.time;
                        return (
                            <div className="flex pl-6 pr-6 pt-4 pb-4 items-start justify-between w-2/3 mh-24 mb-4 border-gray-300 border-solid shadow-sm rounded-lg" style={{ borderWidth: '1px', maxWidth: '628px' }} key={index}>
                                <div className="flex flex-col text-sm text-gray-500" style={{ width: '130px' }}>
                                    {
                                        typeof getOverrideDate(item) === 'string' ? <span className="text-gray-900 font-medium text-sm">{getOverrideDate(item)}</span> : (getOverrideDate(item) as string[]).map((date) => {
                                            return <span className="text-gray-900 font-medium text-sm" key={date}>{date}</span>;
                                        })
                                    }
                                </div>
                                <div className="flex flex-col">
                                    {
                                        timeArr && timeArr?.map((i: TimeItemType, idx: number) => {
                                            return <span className="text-sm font-normal text-gray-500" key={idx}>{transferTimePicker(i)}</span>;
                                        })
                                    }
                                </div>
                                <div className="flex items-center">
                                    <img className="w-6 h-6 cursor-pointer" onClick={() => editDatesItem(item)} src={pencilIcon} />
                                    <img className="w-6 h-6 cursor-pointer ml-4" onClick={() => deleteBlockoutDatesItem(item)} src={trashIcon} />
                                </div>
                            </div>
                        );
                    })
                }
            </div>
            <Button
                onClick={() => {
                    setOpenAddAppointmentModal(true);
                }}
            >+ Add an override
            </Button>
            {
                openAddAppointmentModal && <AddOverrideModal onCancel={onCancel} onFetchData={fetchData} editItem={editItem} />
            }
        </Spin>
    );
};

export default DatesOverride;
